// JSON coloured as editors do: keys, strings, numbers and true/false/null each in a span of their own class.
// A scan, not one regular expression: it reads strings with their escapes, then the number or literal that follows.

const ENTITIES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

const LITERALS = ['true', 'false', 'null'];

const NUMBER = /^-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/;

export function escapeHtml(text: string): string {
  return text.replaceAll(/[&<>"']/g, character => ENTITIES[character]);
}

const span = (kind: string, text: string) => (text ? `<span class="json-${kind}">${escapeHtml(text)}</span>` : '');

const isWhitespace = (character: string | undefined) => character === ' ' || character === '\n' || character === '\r' || character === '\t';

/** Where the string opened at `start` ends (after its closing quote), its escaped quotes skipped; the text's end when unclosed. */
function stringEnd(json: string, start: number): number {
  let index = start + 1;
  while (index < json.length && json[index] !== '"') {
    index += json[index] === '\\' ? 2 : 1;
  }
  return Math.min(index + 1, json.length);
}

/** The key or string opened at `index`: a key takes the colon after it as punctuation; `next` is where the scan goes on. */
function readString(json: string, index: number): { kind: 'key' | 'string'; text: string; punctuation: string; next: number } {
  const end = stringEnd(json, index);
  let after = end;
  while (isWhitespace(json[after])) {
    after++;
  }
  if (json[after] === ':') {
    return { kind: 'key', text: json.slice(index, end), punctuation: json.slice(end, after + 1), next: after + 1 };
  }
  return { kind: 'string', text: json.slice(index, end), punctuation: '', next: end };
}

/** The HTML of `json` with its tokens in spans; every character of it is escaped, so any text is safe to show. */
export function highlightJson(json: string): string {
  let html = '';
  let punctuation = '';
  let index = 0;
  const token = (kind: string, text: string) => {
    html += span('punctuation', punctuation) + span(kind, text);
    punctuation = '';
  };

  while (index < json.length) {
    const character = json[index];
    if (character === '"') {
      const read = readString(json, index);
      token(read.kind, read.text);
      punctuation = read.punctuation;
      index = read.next;
      continue;
    }
    const literal = LITERALS.find(word => json.startsWith(word, index));
    if (literal) {
      token('literal', literal);
      index += literal.length;
      continue;
    }
    const number = character === '-' || (character >= '0' && character <= '9') ? NUMBER.exec(json.slice(index, index + 64)) : null;
    if (number) {
      token('number', number[0]);
      index += number[0].length;
      continue;
    }
    punctuation += character;
    index++;
  }
  return html + span('punctuation', punctuation);
}
